import { IconName, library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Card, Group, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { WarningIcon } from "@phosphor-icons/react";
import { createFileRoute, useLoaderData, useRouter } from "@tanstack/react-router";
import dayjs from "dayjs";
import { type ComponentProps, useCallback, useEffect, useState } from "react";

import { Plate } from "@/components/@custom/Plate";
import { useTv } from "@/features/tv/hooks";
import { THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED } from "@/pages/App/Expedition/ExpeditionHome/expedTypes";
import { CheckInCalled, useDriverCalledQuery, useGetQueueDelayStatusByCompanyQuery } from "@/store/apis/nextcargo";
import { cn } from "@/utils/cn";

import nextCargoLogo from "./-assets/next-cargo-logo.svg";
import { AttendanceCount } from "./-components/AttendanceCount";
import { Courtyard } from "./-components/Courtyard";
import { TVDriverCard } from "./-components/TVDriverCard";
import { TVLogo, TVLogoType } from "./-components/TVLogo";
import { useDriverCall } from "./-hooks/useDriverCall";
import { DriverCalled, DriverCalledQueue } from "./layout";

library.add(fas);

export const Route = createFileRoute("/tv/")({
  component: DriverTVContent
});

function DriverTVContent() {
  const router = useRouter();
  const is2xl = useMediaQuery("(min-width: 96rem)");
  const { company, attendance } = useLoaderData({ from: "/tv" });
  const { data, isLoading, isSuccess, error } = useDriverCalledQuery();
  const { data: delay, isFetching: isDelayFetching } = useGetQueueDelayStatusByCompanyQuery(
    { id: company.data?.id || 0 },
    { skip: !company.data?.id, pollingInterval: THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED }
  );
  const delayReason = delay?.delay?.reason?.trim() || "Não informado";
  const delayIconName = (delay?.delay?.icon as IconName) || "clock";
  const delayStartedAt = delay?.created_at ? dayjs(delay.created_at).format("HH:mm") : null;

  const {
    consts: { driverList, courtyard }
  } = useTv();

  const tvList = driverList;
  const attendedsLength = attendance.totalAttended;
  const lastDriverCalled: CheckInCalled = JSON.parse(localStorage.getItem("@Tv:lastCalledDriver") || "null");

  const { isCallingDriver, callDriver } = useDriverCall();

  const [sortList, setSortList] = useState(false);
  const [currentDriverCalled, setCurrentDriverCalled] = useState<CheckInCalled>(lastDriverCalled);
  const [driverCalledQueue, setDriverCalledQueue] = useState<DriverCalledQueue>({
    driverCalled: null,
    queue: []
  });

  console.log("🏁 DriversTV - Estado atual driverCalledQueue:", {
    current: driverCalledQueue.driverCalled?.queue?.driver?.name || "null",
    queueLength: driverCalledQueue.queue.length
  });

  const panel = sortList
    ? tvList.filter((it) => Number(it.driverTv.id) !== currentDriverCalled?.id)
    : currentDriverCalled
      ? tvList.filter((it) => Number(it.driverTv.id) !== currentDriverCalled.id)
      : tvList;
  const list = panel.sort((a, b) => dayjs(b.driverTv.called_at).valueOf() - dayjs(a.driverTv.called_at).valueOf()).slice(0, is2xl ? 15 : 10);
  const normalizedCourtyard =
    courtyard?.map((item) => ({
      ...item,
      date: item?.date ? new Date(item.date).toISOString() : "",
      initialDate: item?.initialDate ? new Date(item.initialDate).toISOString() : ""
    })) || [];

  const addDriverToQueue = useCallback((newDriver: DriverCalled) => {
    setDriverCalledQueue((prev) => {
      // Se não há motorista atual sendo chamado, define como atual
      if (!prev.driverCalled) {
        console.log("🎯 DriversTV - Definindo motorista atual:", newDriver.queue?.driver?.name);
        return {
          driverCalled: newDriver,
          queue: prev.queue
        };
      }

      // Se já há motorista sendo chamado, adiciona à fila
      console.log("📝 DriversTV - Adicionando à fila:", newDriver.queue?.driver?.name);
      return {
        driverCalled: prev.driverCalled,
        queue: [...prev.queue, newDriver]
      };
    });
  }, []);

  useEffect(() => {
    (() => {
      if (isLoading) return;

      if (!isSuccess) return console.error(error);

      if (!data.driverCalled) return;
      addDriverToQueue(data?.driverCalled as DriverCalled);
      router.invalidate({ filter: (match) => match.routeId === "/tv" });
      return;
    })();
  }, [data?.driverCalled, isSuccess, isLoading]);

  // Efeito para definir o motorista atual quando não está chamando
  useEffect(() => {
    if (!isCallingDriver && list.length > 0 && !currentDriverCalled) {
      const lastDriver = list[list.length - 1];
      setCurrentDriverCalled(lastDriver.driverTv);
    }
  }, [list, currentDriverCalled, isCallingDriver, driverCalledQueue.driverCalled]);

  useEffect(() => {
    const handleDriverCall = async () => {
      if (!driverCalledQueue.driverCalled) {
        console.log("❌ DriverTVContent - Sem driverCalled, não executando");
        return;
      }

      // Usar o hook para chamar o motorista com callbacks
      await callDriver(driverCalledQueue.driverCalled, {
        onBeforeCall: () => {
          console.log("🔊 DriverTVContent - Preparando para chamar:", driverCalledQueue.driverCalled?.queue?.driver?.name);
          setSortList(true);
        },
        onCallStart: () => {
          console.log("🎤 DriverTVContent - Iniciando chamada para:", driverCalledQueue.driverCalled?.queue?.driver?.name);
          setCurrentDriverCalled(driverCalledQueue.driverCalled!);
        },
        onCallEnd: () => {
          console.log("✅ DriverTVContent - Chamada finalizada para:", driverCalledQueue.driverCalled?.queue?.driver?.name);
          // Pega o próximo da fila e atualiza o estado
          setDriverCalledQueue((prev) => {
            const nextDriver = prev.queue.shift(); // Remove e retorna o primeiro item da fila

            console.log("🔄 DriverTVContent - Próximo motorista da fila:", nextDriver?.queue?.driver?.name || "Nenhum");

            return {
              driverCalled: nextDriver || null,
              queue: [...prev.queue] // Cria nova array com os itens restantes
            };
          });
        }
      });
    };

    handleDriverCall();
  }, [driverCalledQueue.driverCalled]);

  useEffect(() => {
    if (currentDriverCalled) {
      localStorage.setItem("@Tv:lastCalledDriver", JSON.stringify(currentDriverCalled));
    }

    return () => {
      localStorage.removeItem("@Tv:lastCalledDriver");
    };
  }, [currentDriverCalled]);

  return (
    <div className="bg-primary flex min-h-dvh items-center justify-center">
      <div className="mx-auto px-4 max-lg:container 2xl:w-full 2xl:max-w-[2000px]">
        <div
          className={cn(
            "grid grid-cols-[repeat(2,minmax(100px,0.7fr))_repeat(var(--columns-count),minmax(100px,1fr))] grid-rows-[150px_repeat(var(--rows-count),175px)] gap-3",
            "[grid-template-areas:'logos_logos_main_main_main_main''courtyard_courtyard_main_main_main_main''courtyard_courtyard_main_main_main_main''courtyard_courtyard_main_main_main_main']",
            {
              "2xl:[grid-template-areas:'logos_logos_main_main_main_main_main''courtyard_courtyard_main_main_main_main_main''courtyard_courtyard_main_main_main_main_main''courtyard_courtyard_main_main_main_main_main''courtyard_courtyard_main_main_main_main_main']": true,
              "border-2 border-red-500": false
            },
            "[--columns-count:4] [--rows-count:3] 2xl:[--columns-count:5] 2xl:[--rows-count:4]"
          )}
        >
          <div className="grid grid-cols-subgrid place-content-center place-items-center [grid-area:logos]">
            {nextCargoLogo && (
              <div className="max-w-32">
                <img src={nextCargoLogo} alt="NextCargo Logo" />
              </div>
            )}

            {company.data && (
              <div>
                <TVLogo logo={company.data?.id.toString() as TVLogoType} alt={`${company.data?.name} Logo`} />
              </div>
            )}
          </div>

          <Card radius="lg" p="sm" c="#4598c4" className="relative! overflow-visible! bg-current! [grid-area:courtyard]!">
            <Courtyard courtyard={normalizedCourtyard as unknown as ComponentProps<typeof Courtyard>["courtyard"]} />
          </Card>

          <div className={cn("grid grid-cols-subgrid grid-rows-subgrid gap-y-10 [grid-area:main] *:col-span-4 *:row-span-2 2xl:*:col-span-5")}>
            <div className="grid grid-cols-subgrid grid-rows-subgrid">
              <Card radius="lg" bg="#ffdf6c" p="sm" className="col-span-3! row-span-2! 2xl:col-span-4!">
                <Group h="100%">
                  {!delay?.active && (
                    <div className="flex w-full max-w-[50px] items-center justify-center">
                      <Text c="primary" size="5xl" fw="lighter" ta="center" classNames={{ root: cn("-rotate-90") }}>
                        Próximo
                      </Text>
                    </div>
                  )}

                  {currentDriverCalled && !isDelayFetching && (
                    <Card bg="white" radius="md" flex={1} h="100%" p={delay?.active ? 0 : "md"}>
                      <Stack gap="xs" align="center" justify="center" h="100%">
                        {!delay?.active && (
                          <>
                            <Badge color="primary" fw="normal" size="xl">
                              {dayjs(currentDriverCalled.called_at).format("DD/MM • HH[h]mm")}
                            </Badge>

                            <Plate
                              plate={currentDriverCalled.queue.truck.licensePlate}
                              color="text-red-600"
                              slots={{
                                wrapper: { className: cn("rounded-xl border-4 px-2 lg:rounded-3xl lg:border-8 lg:px-12") },
                                plate: { className: cn("text-6xl lg:text-9xl") }
                              }}
                            />

                            <Text size="5xl" fw={300}>
                              {currentDriverCalled.queue.driver?.name}
                            </Text>
                          </>
                        )}

                        {delay?.active && (
                          <Card h="100%" w="100%" radius="md" bg="red" p={{ base: "lg", xl: "xl" }}>
                            <Group h="100%" align="center" justify="space-between" wrap="nowrap" gap="xl">
                              <ThemeIcon color="white" variant="transparent" size="inherit" className="shrink-0 motion-safe:animate-pulse">
                                <WarningIcon size={is2xl ? 220 : 170} weight="fill" />
                              </ThemeIcon>

                              <Stack flex={1} gap="sm" align="flex-start" justify="center">
                                <Text c="white" ta="left" tt="uppercase" fw="bolder" size="6xl" lh={1.05}>
                                  Atendimento Pausado,
                                  <br />
                                  Retorno em Breve
                                </Text>

                                {delayStartedAt && (
                                  <Badge color="white" c="red" variant="filled" size="xl" fw={700} radius="xl">
                                    Aviso iniciado às {delayStartedAt}
                                  </Badge>
                                )}
                              </Stack>
                            </Group>
                          </Card>
                        )}
                      </Stack>
                    </Card>
                  )}
                </Group>
              </Card>

              <Card radius="lg" p="sm" className="col-span-1! row-span-2! bg-current! text-[#296218]!">
                <AttendanceCount attendedsLength={attendedsLength} />
              </Card>
            </div>

            {!isDelayFetching && (
              <div className="grid grid-cols-subgrid grid-rows-[repeat(var(--rows-count),minmax(150px,1fr))] gap-x-3 gap-y-8 [--rows-count:2] 2xl:[--rows-count:3]">
                {delay?.active && (
                  <Card radius="lg" bg="transparent" className="col-span-4! row-span-3! 2xl:col-span-5!">
                    <Group h="100%" align="center" justify="center" wrap="nowrap" gap="xl">
                      <ThemeIcon color="white" size={is2xl ? 180 : 140} variant="transparent" className="shrink-0">
                        <FontAwesomeIcon icon={["fas", delayIconName]} size={is2xl ? "10x" : "8x"} />
                      </ThemeIcon>

                      <Title
                        order={1}
                        c="white"
                        ta="left"
                        tt="uppercase"
                        lh={1.05}
                        textWrap="balance"
                        style={{
                          fontSize: "clamp(2.75rem, 7vw, 12rem)",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word"
                        }}
                      >
                        {delayReason}
                      </Title>
                    </Group>
                  </Card>
                )}

                {!delay?.active && (
                  <>
                    {list?.map((driver, i) => {
                      if (is2xl && i >= 15) return null; // Limita a 15 motoristas na tela 2xl

                      return <TVDriverCard key={driver.driverTv.id} driverTv={driver.driverTv} inAttendance={driver.inAttendance} />;
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
